<app-dialog-banner [text]="title" [dialogRef]="dialogRef"></app-dialog-banner>

<!--
    Every step's body is an <ng-template matStepContent>, so Material builds it the first time the
    step is reached rather than up front. That matters for the Mail step: its rich text editor loads
    TinyMCE asynchronously, and eager content had it starting and being torn down again on every
    flick of the mail toggle, which TinyMCE does not survive.

    Each step reads as three bands - the instruction on the dialog's own ground, the fields on the
    tinted panel, the buttons back on white below the rule.
-->
<div class="request-stepper" data-form-ready="true">
    <mat-stepper [linear]="true" [orientation]="isPhone ? 'vertical' : 'horizontal'"
            #stepper (selectionChange)="stepChanged($event)">

        <!-- Only when building from a standard request, and then always first: the template settles
             the wording, the owner and the whole plan, so nothing else can be asked before it. -->
        @if (isTemplated) {
            <mat-step [stepControl]="templateFS.getFormGroup()" i18n-label label="Standard">
                <ng-template matStepContent>
                    <p class="step-help">{{ templateHelp }}</p>
                    <div class="step-body">
                        <form [formGroup]="templateFS.getFormGroup()">
                            <div class="step-row">
                                <div class="step-wide"><app-ctl-holder [control]="templateField.control"></app-ctl-holder></div>
                                @if (schedulesActions) {
                                    <div style="width: 190px"><app-ctl-holder [control]="plannedDateField.control"></app-ctl-holder></div>
                                }
                            </div>
                            @if (schedulesActions) {
                                <p class="step-note">{{ plannedHelp }}</p>
                            }
                        </form>
                    </div>
                    <ng-container *ngTemplateOutlet="nav; context: {back: false, next: hasStepAfter('template'), fs: templateFS}"></ng-container>
                </ng-template>
            </mat-step>
        }

        <mat-step [stepControl]="summaryFS.getFormGroup()" i18n-label label="Summary">
            <ng-template matStepContent>
                <p class="step-help">{{ summaryHelp }}</p>
                <div class="step-body">
                    <form [formGroup]="summaryFS.getFormGroup()">
                        <div class="step-row">
                            <div class="step-wide"><app-ctl-holder [control]="titleField.control"></app-ctl-holder></div>
                            <div style="max-width: 160px"><app-ctl-holder [control]="sourceField.control"></app-ctl-holder></div>
                            <div style="max-width: 120px"><app-ctl-holder [control]="prioField.control"></app-ctl-holder></div>
                        </div>
                        <app-ctl-holder [control]="descriptionField.control"></app-ctl-holder>
                    </form>
                </div>
                <ng-container *ngTemplateOutlet="nav; context: {back: isTemplated, next: hasStepAfter('summary'), fs: summaryFS}"></ng-container>
            </ng-template>
        </mat-step>

        <mat-step [stepControl]="peopleFS.getFormGroup()" i18n-label label="People">
            <ng-template matStepContent>
                <p class="step-help">{{ peopleHelp }}</p>
                <div class="step-body">
                    <form [formGroup]="peopleFS.getFormGroup()">
                        <div class="step-stack">
                            <app-ctl-holder [control]="ownedByField.control"></app-ctl-holder>
                            <app-ctl-holder [control]="unitContact.unitField.control"></app-ctl-holder>
                            <app-ctl-holder [control]="unitContact.personField.control"></app-ctl-holder>
                            <!-- Only once there is a contact to decide about - see ServiceRequest.offerContactLink. -->
                            @if (unitContact.contactLinkField.visible.form) {
                                <app-ctl-holder [control]="unitContact.contactLinkField.control"></app-ctl-holder>
                                <p class="step-note">{{ unitContact.contactLinkField.hint }}</p>
                            }
                        </div>
                    </form>
                </div>
                <ng-container *ngTemplateOutlet="nav; context: {back: true, next: hasStepAfter('people'), fs: peopleFS}"></ng-container>
            </ng-template>
        </mat-step>

        <!-- A case has no supplier, and without one there is no budget and nowhere to send. -->
        @if (isWorkOrder) {
            <mat-step [stepControl]="workFS.getFormGroup()" i18n-label label="Work Order">
                <ng-template matStepContent>
                    <p class="step-help" i18n>
                        Choose the supplier who will carry out the work, and enter the budget figures for how
                        much it is expected to cost.
                    </p>
                    <div class="step-body">
                        <form [formGroup]="workFS.getFormGroup()">
                            <div class="step-row">
                                <div class="step-wide"><app-ctl-holder [control]="supplierField.control"></app-ctl-holder></div>
                                <div style="width: 240px"><app-ctl-holder [control]="mailModeField.control"></app-ctl-holder></div>
                            </div>
                            <app-ctl-holder [control]="budgetField.control"></app-ctl-holder>
                        </form>
                    </div>
                    <ng-container *ngTemplateOutlet="nav; context: {back: true, next: hasStepAfter('work'), fs: workFS}"></ng-container>
                </ng-template>
            </mat-step>
        }

        <!-- Skipped on a phone unless the plan came from a standard request - see hasFollowUps. -->
        @if (hasFollowUps) {
            <mat-step [stepControl]="followUpFS.getFormGroup()" i18n-label label="Follow Ups" #followUpStep>
                <ng-template matStepContent>
                    <p class="step-help" i18n>
                        Set the date you plan to close request, or create your own action plan
                    </p>
                    <div class="step-body">
                        <form [formGroup]="followUpFS.getFormGroup()">
                            <app-ctl-holder [control]="followUpField.control"></app-ctl-holder>
                        </form>
                    </div>
                    <ng-container *ngTemplateOutlet="nav;
                        context: {back: true, next: hasStepAfter('followUp'), fs: followUpFS}"></ng-container>
                </ng-template>
            </mat-step>
        }

        @if (hasFiles) {
        <mat-step i18n-label label="Attach">
            <ng-template matStepContent>
                <p class="step-help">{{ attachHelp }}</p>
                <div class="step-body">
                    <div class="file-list">
                        <table class="file-rows">
                            <tr>
                                <th class="cbx" i18n>Copy</th>
                                @if (mailToSupplier) { <th class="cbx" i18n>Email</th> }
                                <th i18n>Name</th>
                            </tr>
                            @for (file of files; track file.attachment.id) {
                                <tr>
                                    <td class="cbx">
                                        <button type="button" class="cbx-btn" (click)="toggleShare(file)"
                                                i18n-matTooltip matTooltip="Copy this file onto the new request">
                                            <mat-icon color="primary" [attr.data-field]="'share-' + file.attachment.id">{{
                                                file.share ? 'check_box_outline' : 'check_box_outline_blank' }}</mat-icon>
                                        </button>
                                    </td>
                                    @if (mailToSupplier) {
                                        <td class="cbx">
                                            <button type="button" class="cbx-btn" (click)="toggleIncludeInMail(file)"
                                                    i18n-matTooltip matTooltip="Send this file with the email">
                                                <mat-icon color="primary" [attr.data-field]="'mail-' + file.attachment.id">{{
                                                    file.includeInMail ? 'check_box_outline' : 'check_box_outline_blank' }}</mat-icon>
                                            </button>
                                        </td>
                                    }
                                    <td class="fname">
                                        {{ file.attachment.name }}
                                        @if (file.attachment.notes) {
                                            <span class="fnote">{{ file.attachment.notes }}</span>
                                        }
                                    </td>
                                </tr>
                            } @empty {
                                <tr><td class="empty" colspan="3" i18n>There are no files on this request</td></tr>
                            }
                        </table>
                    </div>
                </div>
                <ng-container *ngTemplateOutlet="nav; context: {back: true, next: mailToSupplier, fs: null}"></ng-container>
            </ng-template>
        </mat-step>
        }

        @if (mailToSupplier) {
            <mat-step [stepControl]="mailFS.getFormGroup()" i18n-label label="Mail">
                <ng-template matStepContent>
                    <p class="step-help" i18n>
                        Write the email the supplier will receive, or pick a template to start from.
                    </p>
                    <div class="step-body">
                        <form [formGroup]="mailFS.getFormGroup()">
                            <app-ctl-holder [control]="mailTextField.control"></app-ctl-holder>
                        </form>
                    </div>
                    <ng-container *ngTemplateOutlet="nav; context: {back: true, next: false, fs: null}"></ng-container>
                </ng-template>
            </mat-step>
        }

    </mat-stepper>
</div>

<!-- One button row for every step: the type switch on the left, because it changes what is being
     built rather than moving through it, then Back where there is somewhere to go back to, and
     either Next (gated on that step's own fields, where it has any) or the single save that
     creates the whole request. -->
<ng-template #nav let-back="back" let-next="next" let-fs="fs">
    <div class="step-nav">
        <!-- Not on a phone: repeated on every step it crowds out Back and Next, and the choice has
             already been made on the way in - the New menu offers a case and a work order
             separately, and coming from a supplier it can only be a work order. -->
        @if (!isPhone) {
            <div class="type-switch">
                <span class="type-switch-label" i18n>Raising a</span>
                <mat-button-toggle-group hideSingleSelectionIndicator [value]="requestType"
                        [disabled]="saving" (change)="setRequestType($event.value)"
                        i18n-matTooltip matTooltip="Switch between a case and a work order at any point - nothing you have entered is lost">
                    @for (type of requestTypes; track type.id) {
                        <mat-button-toggle [value]="type.id" [attr.data-field]="'requestType-' + type.id">
                            <mat-icon>{{ type.icon }}</mat-icon>{{ type.name }}
                        </mat-button-toggle>
                    }
                </mat-button-toggle-group>
            </div>
        }
        <!-- matStepContent builds a step's content the first time it is reached and then keeps it,
             so a walkthrough that has moved on leaves the earlier steps' buttons in the DOM,
             hidden. Tests address these attributes plus visibility, never the attribute alone. -->
        @if (back) {
            <button mat-raised-button color="accent" data-step-back
                    (click)="stepper.previous()" i18n>Back</button>
        }
        @if (next) {
            <button mat-raised-button color="primary" data-step-next
                    [disabled]="fs && !fs.getFormGroup().valid"
                    (click)="stepper.next()" i18n>Next</button>
        } @else {
            <button mat-raised-button color="primary" data-save-button
                    [disabled]="!canFinish" (click)="finish()">{{ finishLabel }}</button>
        }
    </div>
</ng-template>
